import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Lock, KeyRound, MailCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordStrength } from '@/components/auth/password-strength';
import { newPasswordSchema } from '@/lib/auth-schema';

// 010 · Recuperación de contraseña (pareja + proveedor). Página pública.
// - Sin sesión de recuperación → formulario de SOLICITUD (email + captcha, mensaje neutro).
// - Con sesión de recuperación (evento PASSWORD_RECOVERY del enlace) → FIJAR nueva contraseña.
// - Enlace inválido/expirado (error en el hash) → aviso + reofrecer solicitud.

const TURNSTILE_DEV_SITE_KEY = '1x00000000000000000000AA';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? TURNSTILE_DEV_SITE_KEY;

type Mode = 'request' | 'reset' | 'error';

// A nivel de módulo (referencia estable): si se define dentro del componente, cada render
// crea una función nueva → React desmonta/remonta el subárbol (input + Turnstile) en cada
// tecla, y el widget parpadea y roba el foco.
const Shell = ({ children }: { children: React.ReactNode }) => (
  <Layout>
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">{children}</Card>
    </div>
  </Layout>
);

const ResetPassword = () => {
  const navigate = useNavigate();
  const { requestPasswordReset, updatePassword } = useAuth();

  const [mode, setMode] = useState<Mode>('request');
  const [linkError, setLinkError] = useState<string | null>(null);

  // Solicitud
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  // Fijar nueva contraseña
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    // Enlace inválido/expirado: Supabase deja error en el hash de la URL.
    const hash = window.location.hash ?? '';
    if (/error=/.test(hash)) {
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      setLinkError(
        params.get('error_description')?.replace(/\+/g, ' ') ??
          'El enlace no es válido o expiró.',
      );
      setMode('error');
      return;
    }

    // Entramos al modo "fijar contraseña" SOLO si llegamos por el enlace de recuperación:
    // el token viene en el hash (type=recovery / access_token) o Supabase emite PASSWORD_RECOVERY.
    // NUNCA por una sesión normal: un usuario logueado que pulsa "¿Olvidaste tu contraseña?"
    // debe ver el formulario de SOLICITUD (pedir el email), no saltar a fijar contraseña.
    if (/type=recovery|access_token=/.test(hash)) {
      setMode('reset');
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setMode('reset');
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error('Completa la verificación para continuar');
      return;
    }
    setRequestLoading(true);
    // Anti-enumeración (C1): no ramificamos por el resultado; siempre el mismo mensaje neutro.
    await requestPasswordReset(email, captchaToken);
    setSent(true);
    setRequestLoading(false);
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = newPasswordSchema.safeParse({ password, confirmPassword });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Datos inválidos');
      return;
    }
    setResetLoading(true);
    const { error } = await updatePassword(password);
    if (error) {
      toast.error('No se pudo actualizar la contraseña: ' + error.message);
      setResetLoading(false);
      return;
    }
    // C5 · invalidar las demás sesiones (mantener la actual).
    await supabase.auth.signOut({ scope: 'others' });
    toast.success('Contraseña actualizada. ¡Bienvenido de nuevo!');
    // C6 · redirect por rol.
    const { data: { user } } = await supabase.auth.getUser();
    let isVendor = false;
    if (user) {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'vendor')
        .maybeSingle();
      isVendor = !!data;
    }
    navigate(isVendor ? '/backoffice' : '/dashboard');
  };

  // Enlace inválido/expirado
  if (mode === 'error') {
    return (
      <Shell>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-display">Enlace no válido</CardTitle>
          <CardDescription>{linkError ?? 'El enlace de recuperación no es válido o ya expiró.'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button className="w-full" onClick={() => { setMode('request'); setSent(false); setLinkError(null); }}>
            Solicitar un enlace nuevo
          </Button>
        </CardContent>
      </Shell>
    );
  }

  // Fijar nueva contraseña
  if (mode === 'reset') {
    return (
      <Shell>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <KeyRound className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-display">Nueva contraseña</CardTitle>
          <CardDescription>Elige una contraseña segura para tu cuenta.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Mínimo 10 caracteres, 1 mayúscula y 1 número"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <PasswordStrength password={password} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Repite tu contraseña"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={resetLoading}>
              {resetLoading ? 'Actualizando...' : 'Actualizar contraseña'}
            </Button>
          </form>
        </CardContent>
      </Shell>
    );
  }

  // Solicitud enviada (mensaje neutro, anti-enumeración)
  if (sent) {
    return (
      <Shell>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <MailCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-display">Revisa tu correo</CardTitle>
          <CardDescription>
            Si el correo está registrado, te enviamos un enlace para restablecer tu contraseña.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground text-center">
            Puede tardar un minuto en llegar. Si no lo ves, revisa spam.
          </p>
        </CardContent>
      </Shell>
    );
  }

  // Solicitud (formulario)
  return (
    <Shell>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <KeyRound className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-2xl font-display">Recuperar contraseña</CardTitle>
        <CardDescription>
          Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleRequest} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recovery-email">Correo electrónico</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="recovery-email"
                type="email"
                placeholder="tu@email.com"
                className="pl-10"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="flex justify-center">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
              options={{ theme: 'auto' }}
            />
          </div>
          <Button type="submit" className="w-full" disabled={requestLoading || !captchaToken}>
            {requestLoading ? 'Enviando...' : 'Enviar enlace'}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={() => navigate('/auth')}>
            Volver a iniciar sesión
          </Button>
        </form>
      </CardContent>
    </Shell>
  );
};

export default ResetPassword;
